import { get } from 'lodash'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import ConfirmationOverlay from '@/components/ConfirmationOverlay'
import Page from '@/components/Page'
import { useAppDispatch, useAppSelector } from '@/store/hooks'

import * as actions from './actions'
import { FETCH_CHANNEL_CONNECTIONS, UPDATE_CHANNEL_CONNECTION } from './actionTypes'
import CreateChannelConnectionModal from './CreateChannelConnection/CreateChannelConnectionModal'
import { ChannelConnection } from './interfaces'
import LandingPage from './LandingPage'
import messages from './messages'
import { getChannelConnectionsFetchData, getCurrentChannelConnection } from './selectors'
import Summary from './Summary'

const errorsToHandle = [
  { type: FETCH_CHANNEL_CONNECTIONS, data: { detailCode: 'FTBFAL' } },
  { type: UPDATE_CHANNEL_CONNECTION, data: { detailCode: 'UTBFAL' } }
]

const StyledContainer: any = styled.div`
  background-color: #ffffff;
  width: 100%;
`

const StyledPage: any = styled(Page)`
  background-color: #ffffff;
  justify-content: flex-start;
  display: flex;
  margin-left: auto;
  margin-right: auto;

  .card {
    padding-top: 0;
    padding-left: 20px;
    padding-right: 20px;
    box-shadow: none;
    border: none;
  }
  .content {
    background-color: #ffffff;
    width: 100%;
  }
`

const OrganizationChannelConnectionsPage = () => {
  const channelConnectionFetchState = useAppSelector(getChannelConnectionsFetchData)
  const currentChannelConnection = useAppSelector(getCurrentChannelConnection)

  const dispatch = useAppDispatch()
  const [isCreatingChannelConnection, setIsCreatingChannelConnection] = useState(false)

  const [confirmDelete, setConfirmDelete] = useState<
    | {
        id: string
        title: string
        message: string
        confirmMessage: string
        cancelMessage: string
        onConfirm: (id: string) => void
      }
    | undefined
  >()

  useEffect(() => {
    if (!channelConnectionFetchState.isCompleted) {
      dispatch(actions.fetchChannelConnections())
    }
  }, [dispatch, channelConnectionFetchState.isCompleted])

  const onDeleteChannelConnection = async (id: string) => {
    await dispatch(actions.deleteChannelConnection(id))
    await dispatch(actions.fetchChannelConnections())
  }

  const onCreatingChannelConnection = async () => {
    setIsCreatingChannelConnection(true)
  }

  const editChannelConnection = async (channelConnection: ChannelConnection) => {
    await dispatch(actions.updateChannelConnection(channelConnection))
  }

  const selectedChannelConnection = async (channelConnection: ChannelConnection) => {
    await dispatch(actions.setCurrentChannelConnection(channelConnection))
  }

  const clearSelectedChannel = async () => {
    await dispatch(actions.clearCurrentChannelConnection())
  }

  const onCreateChannelConnection = async (values: any) => {
    const type = get(values, 'type')

    const channelConnection = {
      name: get(values, 'name'),
      type: type,
      claimValueTenantId: get(values, 'claimValueTenantId'),
      jwksUri: get(values, 'jwksUri'),
      issuer: 'https://igloosoftware.com',
      claimNameTenantId: 'tenantId',
      claimNameUsername: 'username',
      currentChannelConnection: false
    }

    setIsCreatingChannelConnection(false)

    dispatch(actions.createChannelConnection(channelConnection))
  }

  return (
    <StyledContainer>
      {isCreatingChannelConnection && (
        <CreateChannelConnectionModal
          onSubmit={onCreateChannelConnection}
          isOpen={isCreatingChannelConnection}
          onClose={() => setIsCreatingChannelConnection(false)}
        />
      )}
      {confirmDelete && (
        <ConfirmationOverlay
          isWarning={true}
          onConfirm={() => {
            confirmDelete?.onConfirm(confirmDelete?.id)
            setConfirmDelete(undefined)
          }}
          onCancel={() => setConfirmDelete(undefined)}
          message={confirmDelete.title}
          subMessage={confirmDelete.message}
          onConfirmActionTitle={confirmDelete.confirmMessage}
          onCancelActionTitle={confirmDelete.cancelMessage}
        />
      )}
      <StyledPage
        className="organization-channel-connections"
        errorsToHandle={errorsToHandle}
        isLoading={
          channelConnectionFetchState.isLoading ||
          channelConnectionFetchState.isDeleting ||
          channelConnectionFetchState.isSaving
        }
        loadingMessage={<FormattedMessage {...messages.loading} />}
      >
        {channelConnectionFetchState.channelConnections?.length > 0 ? (
          <Summary
            channelConnections={channelConnectionFetchState.channelConnections}
            onDeleteChannel={onDeleteChannelConnection}
            setConfirmDelete={setConfirmDelete}
            onCreateChannel={onCreatingChannelConnection}
            onSelectChannel={selectedChannelConnection}
            clearSelectedChannel={clearSelectedChannel}
            currentChannelConnection={currentChannelConnection}
            onEditChannelConnection={editChannelConnection}
          />
        ) : (
          <LandingPage onCreateChannel={onCreatingChannelConnection} />
        )}
      </StyledPage>
    </StyledContainer>
  )
}

OrganizationChannelConnectionsPage.displayName = 'OrganizationChannelConnectionsPage'
export default OrganizationChannelConnectionsPage
